import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Typography, Box, TextField, Button, CircularProgress, Alert,
  Card, CardHeader, CardContent, Divider, Grid, Paper, IconButton, Tooltip,
  Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  InputAdornment, FormControlLabel, Checkbox, MenuItem, Select, FormControl, InputLabel,
  Radio, RadioGroup, FormLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import ClientCreateDialog from './dialogs/ClientCreateDialog';
import ProductCreateInline from './components/order/ProductCreateInline';
import BarcodeScannerDialog from './dialogs/BarcodeScannerDialog';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Link from '@mui/material/Link';

const theme = createTheme({ direction: 'rtl' });

const fetchClients = async (searchTerm = '') => {
  const params = { light: true };
  if (searchTerm) params.search = searchTerm;
  const { data } = await API.get('/clients/', { params });
  return data;
};

const fetchProducts = async (searchTerm = '') => {
  const params = { light: true };
  if (searchTerm) params.search = searchTerm;
  const { data } = await API.get('/products/', { params });
  return data;
};

// MUI Autocomplete's default filterOptions only matches against the
// string returned by getOptionLabel — here that's ProductName + an
// optional CatalogNumber parenthetical. Barcode is not in the label
// (we don't want visible digit strings cluttering the dropdown), so
// typing a barcode would get filtered out client-side even when the
// backend search correctly returned the product. This replacement
// matches the user's query against every searchable field.
const productFilterOptions = (options, { inputValue }) => {
  const q = (inputValue || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((opt) => {
    const hay = [opt.ProductName, opt.CatalogNumber, opt.Barcode, opt.ProductID]
      .filter((v) => v != null && v !== '')
      .map((v) => String(v).toLowerCase());
    return hay.some((s) => s.includes(q));
  });
};

const NewQuotationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useUser();
  const isSubmittingRef = useRef(false);

  const uiConfig = userProfile?.ui_config || {};
  const orderConfig = userProfile?.group?.order_config || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};
  const userId = userProfile?.username || 'unknown';
  // Share the same localStorage keys as orders so preference carries over
  const TAX_STATE_KEY = `orderTaxState-${userId}`;
  const ROUNDING_KEY = `orderRoundingEnabled-${userId}`;

  // Pre-selected client from navigation state
  const preSelectedClient = location.state?.client || null;

  // Form state
  const [selectedClient, setSelectedClient] = useState(preSelectedClient);
  const [clientSearch, setClientSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productEntries, setProductEntries] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [itemNotes, setItemNotes] = useState('');

  // Settings
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [taxState, setTaxState] = useState('none'); // none, included, excluded
  const [taxRate] = useState(18);
  const [roundingEnabled, setRoundingEnabled] = useState(false);
  const [validUntil, setValidUntil] = useState(null);
  const [notes, setNotes] = useState('');

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showClientCreate, setShowClientCreate] = useState(false);
  const [showProductCreate, setShowProductCreate] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;

  const queryClient = useQueryClient();

  // Load saved tax/rounding preferences
  useEffect(() => {
    const savedTax = localStorage.getItem(TAX_STATE_KEY);
    if (savedTax && ['none', 'included', 'excluded'].includes(savedTax)) {
      setTaxState(savedTax);
    }
    const savedRounding = localStorage.getItem(ROUNDING_KEY);
    if (savedRounding) {
      setRoundingEnabled(savedRounding === 'true');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTaxStateChange = (event) => {
    const newState = event.target.value;
    setTaxState(newState);
    localStorage.setItem(TAX_STATE_KEY, newState);
  };

  const handleRoundingChange = (event) => {
    const enabled = event.target.checked;
    setRoundingEnabled(enabled);
    localStorage.setItem(ROUNDING_KEY, enabled.toString());
  };

  // Queries
  const { data: clients = [] } = useQuery({
    queryKey: ['clients', 'autocomplete', clientSearch],
    queryFn: () => fetchClients(clientSearch),
    staleTime: 5 * 60 * 1000,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'autocomplete', productSearch],
    queryFn: () => fetchProducts(productSearch),
    staleTime: 5 * 60 * 1000,
  });

  // Set unit price when product is selected
  useEffect(() => {
    if (selectedProduct) {
      setUnitPrice(String(selectedProduct.Price || ''));
    }
  }, [selectedProduct]);

  const handleAddProduct = () => {
    if (!selectedProduct) return;
    const qty = parseFloat(quantity) || 1;
    const price = parseFloat(unitPrice) || 0;

    setProductEntries(prev => [
      ...prev,
      {
        id: Date.now(),
        Product: selectedProduct.ProductID,
        ProductName: selectedProduct.ProductName,
        Quantity: qty,
        UnitPrice: price,
        Subtotal: qty * price,
        Notes: itemNotes,
      }
    ]);

    setSelectedProduct(null);
    setQuantity('1');
    setUnitPrice('');
    setItemNotes('');
    setProductSearch('');
  };

  const handleScannedProductAdd = ({ product, quantity: qty, unitPrice: price }) => {
    if (!product) return;
    setProductEntries(prev => [
      ...prev,
      {
        id: Date.now(),
        Product: product.ProductID,
        ProductName: product.ProductName,
        Quantity: qty,
        UnitPrice: price,
        Subtotal: qty * price,
        Notes: '',
      },
    ]);
  };

  const handleRemoveProduct = (id) => {
    setProductEntries(prev => prev.filter(item => item.id !== id));
  };

  const handleProductCreated = (newProduct) => {
    setSelectedProduct(newProduct);
    setUnitPrice(String(newProduct.Price ?? ''));
    setProductSearch('');
    setShowProductCreate(false);
    queryClient.invalidateQueries({ queryKey: ['products', 'autocomplete'] });
  };

  const handleUpdateProduct = (id, field, value) => {
    setProductEntries(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      const qty = field === 'Quantity' ? (parseFloat(value) || 0) : item.Quantity;
      const price = field === 'UnitPrice' ? (parseFloat(value) || 0) : item.UnitPrice;
      updated.Subtotal = qty * price;
      return updated;
    }));
  };

  // Calculate totals
  const subtotal = useMemo(() => {
    return productEntries.reduce((sum, item) => sum + item.Subtotal, 0);
  }, [productEntries]);

  const calculatedTotals = useMemo(() => {
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = (subtotal * (parseFloat(discountValue) || 0)) / 100;
    } else if (discountType === 'fixed') {
      discountAmount = parseFloat(discountValue) || 0;
    }

    const afterDiscount = subtotal - discountAmount;
    let netAmount = afterDiscount;
    let taxAmount = 0;
    let total = afterDiscount;

    if (taxState === 'included') {
      netAmount = afterDiscount / (1 + taxRate / 100);
      taxAmount = afterDiscount - netAmount;
      total = afterDiscount;
    } else if (taxState === 'excluded') {
      netAmount = afterDiscount;
      taxAmount = afterDiscount * (taxRate / 100);
      total = netAmount + taxAmount;
    }

    if (roundingEnabled) {
      total = Math.floor(total);
    }

    return { discountAmount, netAmount, taxAmount, total };
  }, [subtotal, discountType, discountValue, taxState, taxRate, roundingEnabled]);

  const formatDateLocal = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    setError('');

    if (!selectedClient) {
      setError('יש לבחור לקוח');
      return;
    }
    if (productEntries.length === 0) {
      setError(`יש להוסיף לפחות ${productLabel} אחד`);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const payload = {
        ClientBusinessID: selectedClient.ClientBusinessID,
        items: productEntries.map(item => ({
          Product: item.Product,
          Quantity: item.Quantity,
          UnitPrice: item.UnitPrice,
          Notes: item.Notes || '',
        })),
        Notes: notes,
        IsTaxable: taxState !== 'none',
        IsTaxIncluded: taxState === 'included',
        TaxRate: taxRate,
        RoundingEnabled: roundingEnabled,
      };

      if (discountType === 'percentage') {
        payload.DiscountPercentage = parseFloat(discountValue) || 0;
      } else if (discountType === 'fixed') {
        payload.DiscountAmount = parseFloat(discountValue) || 0;
      }
      if (discountReason) {
        payload.DiscountReason = discountReason;
      }
      if (validUntil) {
        payload.ValidUntil = formatDateLocal(validUntil);
      }

      const { data } = await API.post('/quotation/create/', payload);
      navigate(`/quotation/${data.QuotationID}`);
    } catch (err) {
      const errMsg = err?.response?.data?.error || 'שגיאה ביצירת הצעת המחיר';
      setError(errMsg);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);
  };

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle', direction: 'ltr' }} />
              הצעת מחיר חדשה
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/quotations')}
              sx={{ direction: 'ltr' }}
            >
              חזרה
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Client Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title={<Typography variant="h6"><PersonIcon sx={{ mr: 1, verticalAlign: 'middle', direction: 'ltr' }} />בחירת לקוח</Typography>}
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => `${option.ClientName} (${option.ClientBusinessID})`}
                  value={selectedClient}
                  onChange={(_, newValue) => setSelectedClient(newValue)}
                  onInputChange={(_, value) => setClientSearch(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="חיפוש לקוח" placeholder="הקלד שם או ח.פ..." />
                  )}
                  isOptionEqualToValue={(option, value) => option.ClientBusinessID === value?.ClientBusinessID}
                  noOptionsText="לא נמצאו לקוחות"
                />
                <Tooltip title="יצירת לקוח חדש">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setShowClientCreate(true)}
                    sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                  >
                    לקוח חדש
                  </Button>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>

          {/* Products Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title={<Typography variant="h6">הוספת {productPluralLabel}</Typography>}
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Autocomplete
                  sx={{ flex: 2, minWidth: 220 }}
                  options={products}
                  getOptionLabel={(option) => `${option.ProductName}${option.CatalogNumber ? ` (${option.CatalogNumber})` : ''}`}
                  filterOptions={productFilterOptions}
                  value={selectedProduct}
                  onChange={(_, newValue) => setSelectedProduct(newValue)}
                  onInputChange={(_, value) => setProductSearch(value)}
                  renderInput={(params) => (
                    <TextField {...params} label={`חיפוש ${productLabel}`} size="small" />
                  )}
                  isOptionEqualToValue={(option, value) => option.ProductID === value?.ProductID}
                  noOptionsText={`לא נמצאו ${productPluralLabel}`}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowProductCreate(true)}
                  sx={{ direction: 'ltr', whiteSpace: 'nowrap', alignSelf: 'center' }}
                >
                  {`${productLabel} חדש`}
                </Button>
                {barcodeEnabled && (
                  <Tooltip title="סריקת ברקוד">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={() => setScannerOpen(true)}
                      sx={{ direction: 'ltr', whiteSpace: 'nowrap', alignSelf: 'center' }}
                    >
                      סרוק
                    </Button>
                  </Tooltip>
                )}
                <TextField
                  label="כמות"
                  type="number"
                  size="small"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    minWidth: 90,
                    '& input': {
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      MozAppearance: 'textfield',
                    },
                  }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                />
                <TextField
                  label="מחיר"
                  type="number"
                  size="small"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onWheel={(e) => e.target.blur()}
                  sx={{
                    minWidth: 110, direction: 'ltr',
                    '& input': {
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      MozAppearance: 'textfield',
                    },
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₪</InputAdornment>
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                  disabled={!selectedProduct}
                  size="small"
                  sx={{ direction: 'ltr', alignSelf: 'center' }}
                >
                  הוסף
                </Button>
              </Box>

              {productEntries.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{productLabel}</TableCell>
                        <TableCell>כמות</TableCell>
                        <TableCell>מחיר יחידה</TableCell>
                        <TableCell>סה"כ</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productEntries.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{item.ProductName}</TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.Quantity}
                              onChange={(e) => handleUpdateProduct(item.id, 'Quantity', parseFloat(e.target.value) || 0)}
                              onFocus={(e) => e.target.select()}
                              onWheel={(e) => e.target.blur()}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              sx={{
                                width: 80,
                                '& input': {
                                  textAlign: 'center',
                                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                  MozAppearance: 'textfield',
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ p: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.UnitPrice}
                                onChange={(e) => handleUpdateProduct(item.id, 'UnitPrice', parseFloat(e.target.value) || 0)}
                                onFocus={(e) => e.target.select()}
                                onWheel={(e) => e.target.blur()}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">₪</InputAdornment>,
                                }}
                                sx={{
                                  width: 110, direction: 'ltr',
                                  '& input': {
                                    textAlign: 'center',
                                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                    MozAppearance: 'textfield',
                                  },
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>{formatCurrency(item.Subtotal)}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveProduct(item.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Settings & Totals Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title={<Typography variant="h6">הגדרות והערות</Typography>} />
            <CardContent>
              <Grid container spacing={2}>
                {/* Discount */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>הנחה</InputLabel>
                    <Select
                      value={discountType}
                      label="הנחה"
                      onChange={(e) => { setDiscountType(e.target.value); setDiscountValue(''); }}
                    >
                      <MenuItem value="none">ללא הנחה</MenuItem>
                      <MenuItem value="percentage">אחוז הנחה</MenuItem>
                      <MenuItem value="fixed">סכום קבוע</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {discountType !== 'none' && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={discountType === 'percentage' ? 'אחוז הנחה' : 'סכום הנחה'}
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {discountType === 'percentage' ? '%' : '₪'}
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                )}
                {discountType !== 'none' && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth size="small" label="סיבת הנחה"
                      value={discountReason} onChange={(e) => setDiscountReason(e.target.value)}
                    />
                  </Grid>
                )}

                {/* Tax */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" gutterBottom>מע"מ</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={taxState}
                      onChange={handleTaxStateChange}
                    >
                      <FormControlLabel value="none" control={<Radio />} label="ללא" />
                      <FormControlLabel value="included" control={<Radio />} label='כולל מע"מ' />
                      <FormControlLabel value="excluded" control={<Radio />} label='חייב במע"מ - לא כולל' />
                    </RadioGroup>
                  </FormControl>
                  {taxState !== 'none' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      שיעור מע"מ: {taxRate}%
                    </Typography>
                  )}
                </Grid>

                {/* Rounding */}
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={<Checkbox checked={roundingEnabled} onChange={handleRoundingChange} />}
                    label="עיגול למטה"
                  />
                </Grid>

                {/* Valid Until */}
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="תוקף עד (אופציונלי)"
                    value={validUntil}
                    onChange={setValidUntil}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { direction: 'ltr' } } }
                    }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth multiline rows={3} label="הערות"
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Totals Summary */}
          {productEntries.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ maxWidth: { xs: '100%', sm: 350 }, mr: { xs: 0, sm: 'auto' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography color="text.secondary">סה"כ לפני הנחה:</Typography>
                    <Typography>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  {calculatedTotals.discountAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography color="text.secondary">הנחה:</Typography>
                      <Typography color="error">-{formatCurrency(calculatedTotals.discountAmount)}</Typography>
                    </Box>
                  )}
                  {taxState !== 'none' && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography color="text.secondary">סכום לפני מע"מ:</Typography>
                        <Typography>{formatCurrency(calculatedTotals.netAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography color="text.secondary">מע"מ ({taxRate}%):</Typography>
                        <Typography>{formatCurrency(calculatedTotals.taxAmount)}</Typography>
                      </Box>
                    </>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">סה"כ:</Typography>
                    <Typography variant="h6" fontWeight="bold">{formatCurrency(calculatedTotals.total)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedClient || productEntries.length === 0}
              sx={{ minWidth: 200, direction: 'ltr' }}
            >
              {isSubmitting ? 'שומר...' : 'צור הצעת מחיר'}
            </Button>
          </Box>

          {/* Client Create Dialog */}
          <ClientCreateDialog
            open={showClientCreate}
            onClose={() => setShowClientCreate(false)}
            onClientCreated={(newClient) => {
              setSelectedClient(newClient);
              setShowClientCreate(false);
            }}
            userProfile={userProfile}
          />

          {/* Product Create Dialog — same component used in the order flow */}
          <ProductCreateInline
            open={showProductCreate}
            onClose={() => setShowProductCreate(false)}
            productLabel={productLabel}
            productFields={productFields}
            productFieldsSchema={productFieldsSchema}
            storageEnabled={false}
            allStorages={[]}
            onCreated={handleProductCreated}
          />

          {barcodeEnabled && (
            <BarcodeScannerDialog
              open={scannerOpen}
              onClose={() => setScannerOpen(false)}
              onAdd={handleScannedProductAdd}
              productLabel={productLabel}
            />
          )}
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default NewQuotationPage;
